import { useState } from "react";
import { useSearchParams } from "react-router";
import { useForm, useStore } from "@tanstack/react-form";
import { Button, Icon } from "@sellar-io/components";
import { useDeliveryPreferencesStore } from "../deliveryPreferencesStore";
import { deliveryContactSchema } from "../stepRegistry";
import { OrderBanner } from "../components/OrderBanner";
import { OrderSummaryDrawer } from "../components/OrderSummaryDrawer";
import { useOrderLinkFlowOrder } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";

const inputClass =
  "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-800 outline-none focus:border-zinc-400";

export default function DeliveryContactStep() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: order } = useOrderLinkFlowOrder(token);
  const store = useDeliveryPreferencesStore();

  const form = useForm({
    defaultValues: {
      email: store.deliveryContact?.email ?? "",
      name: store.deliveryContact?.name ?? "",
      phone: store.deliveryContact?.phone ?? "",
      additionalContacts: store.deliveryContact?.additionalContacts ?? [],
    },
    validators: {
      onSubmit: deliveryContactSchema,
    },
    onSubmit: ({ value }) => {
      store.setDeliveryContact(value);
      store.next();
    },
  });

  const additionalContacts = useStore(
    form.store,
    (s) => s.values.additionalContacts,
  );

  const addContact = () => {
    form.setFieldValue("additionalContacts", [
      ...additionalContacts,
      { name: "", email: "", phone: "" },
    ]);
  };

  const removeContact = (index: number) => {
    form.setFieldValue(
      "additionalContacts",
      additionalContacts.filter((_, i) => i !== index),
    );
  };

  return (
    <div className="flex min-h-screen flex-col">
      {order && (
        <OrderBanner order={order} onShowDetails={() => setDrawerOpen(true)} />
      )}

      <div className="w-full flex-1 border-t border-zinc-200 bg-neutral-100 px-5 py-8">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-5">
            <button
              type="button"
              onClick={() => store.prev()}
              className="mb-3 flex cursor-pointer items-center gap-2 text-zinc-800 hover:text-zinc-600"
            >
              <Icon icon="CHEVRON_LEFT" size={0.9} />
              <h2 className="text-xl font-semibold">Delivery contact</h2>
            </button>
            <p className="mt-1 text-sm text-zinc-500">
              Who should we contact with any updates to do with this delivery?
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            {/* Primary contact fields */}
            <form.Field name="email">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Email address
                  </label>
                  <input
                    className={inputClass}
                    type="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="name">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Contact name
                  </label>
                  <input
                    className={inputClass}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="phone">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Phone number
                  </label>
                  <input
                    className={inputClass}
                    type="tel"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Additional contacts */}
            {additionalContacts.map((_, index) => (
              <div key={index} className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-zinc-600">
                    Additional contact {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeContact(index)}
                    className="cursor-pointer text-sm text-red-500 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <form.Field name={`additionalContacts[${index}].email`}>
                  {(field) => (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-zinc-800">
                        Email address
                      </label>
                      <input
                        className={inputClass}
                        type="email"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-red-500">
                          {field.state.meta.errors
                            .filter(Boolean)
                            .map((e) => (typeof e === "string" ? e : e.message))
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
                <form.Field name={`additionalContacts[${index}].name`}>
                  {(field) => (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-zinc-800">
                        Contact name
                      </label>
                      <input
                        className={inputClass}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-red-500">
                          {field.state.meta.errors
                            .filter(Boolean)
                            .map((e) => (typeof e === "string" ? e : e.message))
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
                <form.Field name={`additionalContacts[${index}].phone`}>
                  {(field) => (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-zinc-800">
                        Phone number
                      </label>
                      <input
                        className={inputClass}
                        type="tel"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-red-500">
                          {field.state.meta.errors
                            .filter(Boolean)
                            .map((e) => (typeof e === "string" ? e : e.message))
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              </div>
            ))}

            {additionalContacts.length < 3 && (
              <button
                type="button"
                onClick={addContact}
                className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-zinc-800"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-800 text-white">
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </span>
                Add another delivery contact
              </button>
            )}

            <Button
              type="submit"
              className="mt-1 w-full rounded-xl bg-zinc-900 py-3.5 text-sm font-semibold tracking-wide text-white uppercase transition-colors hover:bg-zinc-800"
            >
              Continue
            </Button>
            <p className="text-center text-xs text-zinc-500">
              You need to continue to save your delivery contact
            </p>
          </form>
        </div>
      </div>

      {order && (
        <OrderSummaryDrawer
          active={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          order={order}
        />
      )}
    </div>
  );
}
